/**
 * Dates in the reader's language (BUG-40).
 *
 * `toLocaleDateString()` with no argument formats in the *browser's* locale, so
 * a tester with an en-US browser saw `9/10/2026` on both the Russian and the
 * Estonian interface — while «Главная», which passed the language explicitly,
 * printed «10 сент. 2026» for the very same orders. Every product surface goes
 * through here now, so one screen cannot disagree with the next.
 */
export type DateStyle = "short" | "medium" | "dateTime";

const OPTIONS: Record<DateStyle, Intl.DateTimeFormatOptions> = {
  /** 10.09.2026 — for dense rows and cards. */
  short: { day: "2-digit", month: "2-digit", year: "numeric" },
  /** 10 сент. 2026 — where the month reads better as a word. */
  medium: { day: "2-digit", month: "short", year: "numeric" },
  /** The same, with the clock. */
  dateTime: {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  },
};

/** i18next's language ("ru", "et-EE", …) → a locale Intl will accept. */
export function uiLocale(language: string | undefined): string {
  const lang = (language || "et").split("-")[0];
  if (lang === "ru") return "ru-RU";
  if (lang === "en") return "en-GB"; // day-first, like the other two
  return "et-EE";
}

export function formatDate(
  value: string | number | Date | null | undefined,
  language: string,
  style: DateStyle = "short"
): string {
  if (value === null || value === undefined || value === "") return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(uiLocale(language), OPTIONS[style]);
}

/**
 * A calendar date (`YYYY-MM-DD`) in the reader's language — never an instant.
 *
 * `formatDate` would send it through `new Date("2019-05-04")`, which parses as
 * UTC midnight and then prints in the *browser's* zone: «03.05.2019» for every
 * viewer west of Greenwich. Ticket 20's `documentDate` is a day, not a moment
 * (D9), so the parts are formatted where they cannot shift.
 */
export function formatCalendarDate(day: string | null | undefined, language: string): string {
  if (!day || !/^\d{4}-\d{2}-\d{2}$/.test(day)) return day ?? "";
  const [year, month, date] = day.split("-").map(Number);
  // Local noon: the value is never rendered as an instant, and midday cannot
  // cross a day boundary under any offset or DST shift.
  const at = new Date(year, month - 1, date, 12, 0, 0);
  if (Number.isNaN(at.getTime())) return day;
  return at.toLocaleString(uiLocale(language), OPTIONS.short);
}
